/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL } from '@/config/serviceAPI';
import { getHeaderDataFromCache, HEADER_PRIMARY_KEY } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { YmmeAcesVehicleResponse } from '../interface';
import { setPreferredVehicleCookies } from '@/utils/analytics/dataLayer/preferredVehicleCookies';
import {
  getKey as getVehiclesKey,
  getFullKey as getFullVehiclesKey,
  selector as getVehiclesSelector,
} from '../apiAces/getVehiclesACES';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { stringifyUrl, parseUrl } from '@/utils/urlHelpers';
import type { Locale } from '@/types/i18n';
import { dispatchEvent } from '@/utils/eventPublisher';
import { useGlobalState } from '@/hooks/useGlobalState';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { sessionStorage } from '@/utils/sessionStorage';
import { type AcesVehicleAttributes } from './types';
import { pageLinksToRepairGuides } from '@/features/myVehicle/utils/pageLinksToRepairGuides';

const URL = `${requestBaseURL}/ecomm/b2c/v3/currentUser/vehicles`;

const postVehiclesACES = async (
  options: {
    acesVehicleAttributes?: AcesVehicleAttributes;
    previousVehicleID?: string;
    includeRepairGuides?: boolean;
  },
  axiosInstance?: AxiosInstance,
) => {
  const { acesVehicleAttributes, previousVehicleID, includeRepairGuides } = options;
  const response = await getAxios(axiosInstance).post<YmmeAcesVehicleResponse>(
    URL,
    {
      acesVehicleAttributes,
    },
    {
      params: {
        includeRepairGuides: includeRepairGuides || undefined,
      },
    },
  );
  if (previousVehicleID) {
    sessionStorage.setItem('previousPreferredVehicleId', previousVehicleID);
  }
  return getVehiclesSelector(response.data);
};

export const getKey = () => ['user', 'vehiclesAces'];
type Variables = {
  acesVehicleAttributes?: AcesVehicleAttributes;
  isLicensePlateRequest?: boolean;
};

type MutationOptions = {
  onSuccess?: (data: YmmeAcesVehicleResponse) => void;
  onError?: (error?: Error) => void;
  onUpdateVehicleFinished?: (
    data: YmmeAcesVehicleResponse | undefined,
    error: Error | null,
  ) => void;
};

const onVehiclesSuccess = async (
  queryClient: QueryClient,
  data: YmmeAcesVehicleResponse,
  includeRepairGuides?: boolean,
) => {
  const options = includeRepairGuides ? { includeRepairGuides } : undefined;

  if (includeRepairGuides) {
    queryClient.setQueryData(getFullVehiclesKey(options), data);
    queryClient.setQueryData(getVehiclesKey(), data);
  } else {
    void queryClient.resetQueries({ queryKey: getFullVehiclesKey({ includeRepairGuides: true }) });
    queryClient.setQueryData(getVehiclesKey(), data);
  }

  await queryClient.refetchQueries({
    queryKey: [HEADER_PRIMARY_KEY],
  });

  const headerData = getHeaderDataFromCache(queryClient);
  const preferredVehicle = getPreferredVehicle(headerData);

  if (preferredVehicle) {
    setPreferredVehicleCookies({
      vehicleYear: preferredVehicle.year,
      vehicleMake: preferredVehicle.make,
      vehicleModel: preferredVehicle.model,
      vehicleEngine: preferredVehicle.engine,
      vehicleId: preferredVehicle.catalogVehicleId,
    });
  }
};

export const useVehiclesMutationACES = ({
  onSuccess,
  onError,
  onUpdateVehicleFinished,
}: MutationOptions = {}) => {
  const queryClient = useQueryClient();
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const [, setSearchState] = useGlobalState('search');
  const previousPreferredVehicle = usePreferredVehicle();
  const previousVehicleID = previousPreferredVehicle?.catalogVehicleId ?? '0';

  const includeRepairGuides = pageLinksToRepairGuides(legacyRouter.pathname) || undefined;

  return useMutation({
    mutationFn: ({ acesVehicleAttributes }: Variables) =>
      postVehiclesACES({
        acesVehicleAttributes,
        previousVehicleID,
        includeRepairGuides,
      }),
    mutationKey: getKey(),
    onSuccess: (data: YmmeAcesVehicleResponse) => {
      setSearchState({
        preferredVehicleChanged: true,
      });
      onSuccess?.(data);
      void onVehiclesSuccess(queryClient, data, includeRepairGuides);
      if (legacyRouter.query.currentPage || legacyRouter.query.pageNumber) {
        const { url, query } = parseUrl(legacyRouter.asPath);
        delete query.pageNumber;
        delete query.currentPage;

        const newPathName = stringifyUrl({
          url,
          query,
        });

        void router.push(newPathName);
      }
      dispatchEvent('preferredVehicleUpdated', { vehicleSet: true });
    },
    onError: (error) => {
      onError?.(error);
    },
    onSettled: (data, error) => {
      onUpdateVehicleFinished?.(data, error);
    },
  });
};

export const mutateVehiclesACES = async (
  queryClient: QueryClient,
  options: {
    acesTransitionFlag: boolean;
    acesVehicleAttributes: AcesVehicleAttributes;
    includeRepairGuides?: boolean;
    locale: Locale;
  },
  axiosInstance?: AxiosInstance,
) => {
  const data = await postVehiclesACES(options, axiosInstance);
  await onVehiclesSuccess(queryClient, data, options.includeRepairGuides);
};
